import { useEffect, useState } from 'react';
import type { FieldDefinition, FieldType } from '@grs-atlas/shared';
import { blankField, FIELD_TYPES, fieldNeedsOptions } from './mapDefinitionDefaults';

type Props = {
  fields: FieldDefinition[];
  onChange: (fields: FieldDefinition[]) => void;
  /** Keys that already exist on the saved map — not editable in the UI. */
  lockedKeys?: ReadonlySet<string>;
};

function parseOptions(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function OptionsInput({
  options,
  onCommit,
}: {
  options: string[] | undefined;
  onCommit: (options: string[]) => void;
}) {
  const committed = (options ?? []).join(', ');
  const [draft, setDraft] = useState(committed);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(committed);
    }
  }, [committed, focused]);

  return (
    <label style={{ gridColumn: '1 / -1' }}>
      Options (comma-separated)
      <input
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setFocused(false);
          const next = parseOptions(draft);
          setDraft(next.join(', '));
          onCommit(next);
        }}
        placeholder="e.g. Public Sector, State/Local, Higher Ed"
      />
    </label>
  );
}

export function MapFieldsEditor({ fields, onChange, lockedKeys }: Props) {
  const updateField = (idx: number, patch: Partial<FieldDefinition>) => {
    const next = [...fields];
    next[idx] = { ...fields[idx]!, ...patch };
    onChange(next);
  };

  const removeField = (idx: number) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const addField = () => {
    onChange([...fields, blankField()]);
  };

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      {fields.map((field, idx) => {
        const keyLocked =
          Boolean(field.key) &&
          (lockedKeys?.has(field.key) || field.key === 'name' || field.key === 'stateCodes');
        return (
          <div key={idx} className="panel" style={{ padding: '0.75rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
                gap: '0.5rem',
              }}
            >
              <strong style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                Field {idx + 1}
              </strong>
              <button
                type="button"
                className="btn secondary"
                disabled={
                  fields.length <= 1 || field.key === 'name' || field.key === 'stateCodes'
                }
                onClick={() => removeField(idx)}
              >
                Remove
              </button>
            </div>
            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <label>
                Key
                <input
                  value={field.key}
                  placeholder="e.g. customerName"
                  readOnly={keyLocked}
                  title={
                    keyLocked
                      ? 'Field keys cannot be changed after they are saved. Add a new field instead.'
                      : undefined
                  }
                  style={keyLocked ? { opacity: 0.75, cursor: 'default' } : undefined}
                  onChange={(e) =>
                    updateField(idx, {
                      key: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                    })
                  }
                />
              </label>
              <label>
                Label
                <input
                  value={field.label}
                  placeholder="Display label"
                  onChange={(e) => updateField(idx, { label: e.target.value })}
                />
              </label>
              <label>
                Type
                <select
                  value={field.type}
                  onChange={(e) => {
                    const type = e.target.value as FieldType;
                    updateField(idx, {
                      type,
                      options: fieldNeedsOptions(type) ? (field.options ?? []) : undefined,
                    });
                  }}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(field.required)}
                  onChange={(e) => updateField(idx, { required: e.target.checked })}
                />
                Required
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(field.filterable)}
                  onChange={(e) => updateField(idx, { filterable: e.target.checked })}
                />
                Filterable
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(field.searchable)}
                  onChange={(e) => updateField(idx, { searchable: e.target.checked })}
                />
                Searchable
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(field.tableVisible)}
                  onChange={(e) => updateField(idx, { tableVisible: e.target.checked })}
                />
                Table column
              </label>
              {fieldNeedsOptions(field.type) && (
                <OptionsInput
                  options={field.options}
                  onCommit={(options) => updateField(idx, { options })}
                />
              )}
            </div>
          </div>
        );
      })}
      <button type="button" className="btn secondary" onClick={addField}>
        Add field
      </button>
    </div>
  );
}
